/**
 * Editor clipboard composable
 * Handles copy, cut, and paste functionality for contenteditable editor
 */

import { isWordHTML, parseWordHTML } from '../utils/docs';
import { nodeRegistry } from '../core/NodeRegistry';

/**
 * @param {Object} options
 * @param {import('vue').Ref<HTMLElement>} options.editorRef - Editor element ref
 * @param {Object} options.chatState - Chat state object
 * @param {Function} options.syncContent - Sync content callback
 * @param {Function} options.parseAndInsertText - Parse and insert text function
 * @param {Function} options.insertImage - Insert image function
 * @param {Function} options.removeMaterial - Remove material function
 * @param {Function} options.replaceImagePlaceholder - Replace image placeholder function
 * @param {Function} options.toast - Toast notification function
 * @param {Function} options.emit - Vue emit function
 * @returns {Object} Clipboard handlers
 */
export function useEditorClipboard(options = {}) {
  const {
    editorRef,
    syncContent,
    parseAndInsertText,
    insertImage,
    removeMaterial,
    replaceImagePlaceholder,
    toast,
    emit,
  } = options;

  const toRegistryType = (dataType) => (dataType === 'userskill' ? 'skill' : dataType);

  /**
   * Delete current selection (replaces document.execCommand('delete'))
   */
  const deleteSelection = () => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    if (!range.collapsed) {
      range.deleteContents();
    }
  };

  /**
   * Get selected Material node if any
   * @returns {HTMLElement|null}
   */
  const getSelectedMaterial = () => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return null;

    // Check if selection is in or contains a Material node
    const range = selection.getRangeAt(0);

    // Check if anchorNode or focusNode is inside Material
    let node = selection.anchorNode;
    while (node && node !== editorRef?.value) {
      if (node.nodeType === Node.ELEMENT_NODE && node.getAttribute?.('data-type') === 'image') {
        return node;
      }
      node = node.parentNode;
    }

    // Check if only one Material is fully selected
    if (range.collapsed) return null;

    const container = range.commonAncestorContainer;
    if (container.nodeType === Node.ELEMENT_NODE) {
      const materials = container.querySelectorAll?.('[data-type="image"]');
      if (materials?.length === 1 && range.intersectsNode(materials[0])) {
        return materials[0];
      }
    }

    return null;
  };

  /**
   * Convert selection to text with @SeedeMaterial format
   * @returns {string}
   */
  const getSelectionAsText = () => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return '';

    const range = selection.getRangeAt(0);
    if (range.collapsed) return '';

    // Clone selection content
    const fragment = range.cloneContents();
    const tempDiv = document.createElement('div');
    tempDiv.appendChild(fragment);

    const specialNodes = tempDiv.querySelectorAll(
      '[data-type="image"],[data-type="tag"],[data-type="color"],[data-type="remix"],[data-type="userskill"],[data-type="document"]'
    );
    specialNodes.forEach((node) => {
      const dataType = node.getAttribute('data-type');
      if (!dataType) return;
      const serialized = nodeRegistry.serialize(toRegistryType(dataType), node, {});
      node.parentNode?.replaceChild(document.createTextNode(serialized), node);
    });

    const raw = tempDiv.textContent || '';
    return raw
      .replace(/\r\n?/g, '\n')
      .replace(/\u00A0/g, ' ')
      .replace(/[ \t]+\n/g, '\n')
      .replace(/\n[ \t]+/g, '\n')
      .replace(/\n{3,}/g, '\n\n');
  };

  /**
   * Handle copy event - convert Material to @SeedeMaterial format
   * @param {ClipboardEvent} e
   */
  const handleCopy = (e) => {
    const text = getSelectionAsText();
    if (text) {
      e.preventDefault();
      e.clipboardData.setData('text/plain', text);
    }
  };

  /**
   * Handle cut event - copy then delete selection and sync
   * @param {ClipboardEvent} e
   */
  const handleCut = (e) => {
    const text = getSelectionAsText();
    if (text) {
      e.preventDefault();
      e.clipboardData.setData('text/plain', text);
      // Delete selection
      deleteSelection();
      syncContent?.({ immediate: true });
    }
  };

  /**
   * Convert SVG text to File
   * @param {string} svgText
   * @returns {File}
   */
  const svgTextToFile = (svgText) => {
    const blob = new Blob([svgText], { type: 'image/svg+xml' });
    return new File([blob], `paste_${Date.now()}.svg`, { type: 'image/svg+xml' });
  };

  /**
   * Fetch SVG from URL and return as File
   * @param {string} url
   * @returns {Promise<File|null>}
   */
  const fetchSvgAsFile = async (url) => {
    try {
      const response = await fetch(url);
      if (!response.ok) return null;
      const svgText = await response.text();
      return svgTextToFile(svgText);
    } catch {
      return null;
    }
  };

  /**
   * Handle paste event
   * @param {ClipboardEvent} e
   */
  const handlePaste = async (e) => {
    const clipboardData = e.clipboardData;
    if (!clipboardData) return;

    // Check if Word/Office HTML content
    if (isWordHTML(clipboardData)) {
      e.preventDefault();
      const html = clipboardData.getData('text/html');

      try {
        const parsed = await parseWordHTML(html);

        // Insert text with placeholders first
        if (parsed.text) {
          insertTextAtCursor(parsed.text);
        }

        // Process images - replace placeholders with image nodes
        for (const imageData of parsed.images) {
          if (imageData.file && imageData.id) {
            // Find placeholder in editor and replace with image
            await replaceImagePlaceholder?.(imageData.id, imageData.file);
          }
        }

        syncContent?.({ immediate: true });
        emit?.('paste', e);
        return;
      } catch (error) {
        console.error('Failed to parse Word HTML:', error);
        toast?.({
          title: 'Upload failed',
          description: error.message || 'Content parsing failed',
          variant: 'destructive',
        });
      }
    }

    const items = clipboardData.items;
    if (!items) {
      emit?.('paste', e);
      return;
    }

    // Check clipboard content types
    let hasText = false;
    let hasImage = false;
    let imageItem = null;

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        hasImage = true;
        imageItem = items[i];
      }
      if (items[i].type.indexOf('text') !== -1) {
        hasText = true;
      }
    }

    // Check if text content is SVG
    const textContent = clipboardData.getData('text/plain');
    const htmlContent = clipboardData.getData('text/html');

    // Detect SVG: pure SVG text or SVG image link in HTML
    const isSvgText =
      textContent?.trim().startsWith('<svg') || textContent?.trim().startsWith('<?xml');

    // Detect SVG image in HTML <img src="xxx.svg">
    const svgImgMatch = htmlContent?.match(/<img[^>]+src=["']([^"']+\.svg)["'][^>]*>/i);
    const svgImgUrl = svgImgMatch?.[1] || null;

    // Check if Material is selected
    const selectedMaterial = getSelectedMaterial();

    // Determine if there's SVG content
    const hasSvg = isSvgText || svgImgUrl;

    // If Material selected and has image data or SVG, prioritize image replacement
    if (selectedMaterial && (hasImage || hasSvg)) {
      e.preventDefault();
      let file = null;

      if (hasImage && imageItem) {
        file = imageItem.getAsFile();
      } else if (isSvgText) {
        file = svgTextToFile(textContent);
      } else if (svgImgUrl) {
        file = await fetchSvgAsFile(svgImgUrl);
      }

      if (file) {
        const assetId = selectedMaterial.getAttribute('data-asset-id');
        // Remove selected Material
        removeMaterial?.(assetId);
        // Insert new image
        await insertImage?.(file);
      }
      return;
    }

    // If SVG text or SVG image link, handle as image
    if (hasSvg) {
      e.preventDefault();
      let file = null;
      if (isSvgText) {
        file = svgTextToFile(textContent);
      } else if (svgImgUrl) {
        file = await fetchSvgAsFile(svgImgUrl);
      }
      if (file) {
        await insertImage?.(file);
      }
      return;
    }

    // If no text content, handle image
    if (hasImage && imageItem) {
      e.preventDefault();
      const file = imageItem.getAsFile();
      if (file) {
        await insertImage?.(file);
      }
      return;
    }

    // If has text content (and no Material selected or no image), use text
    if (hasText) {
      const text = clipboardData.getData('text/plain');
      if (text) {
        e.preventDefault();
        // Parse pasted text, extract special markers
        parseAndInsertText?.(text);
      }
      emit?.('paste', e);
      return;
    }

    emit?.('paste', e);
  };

  /**
   * Insert text at cursor position (replaces document.execCommand('insertText'))
   * @param {string} text
   */
  const insertTextAtCursor = (text) => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    range.deleteContents();

    const textNode = document.createTextNode(text);
    range.insertNode(textNode);

    // Move cursor after inserted text
    range.setStartAfter(textNode);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
  };

  return {
    handleCopy,
    handleCut,
    handlePaste,
    deleteSelection,
    getSelectedMaterial,
    getSelectionAsText,
    insertTextAtCursor,
  };
}
